import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet, NavigationEnd } from '@angular/router';
import { CommonModule, TitleCasePipe } from '@angular/common';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { PermissionService } from '../../core/services/permission.service';
import { Permissions } from '../../core/enums/permissions.enum';
import { MenuService } from '../../core/services/menu.service';
import { MenuItem } from '../../core/models/menu-item.model';
import { UserRole, UserRolePermissions } from '../../core/enums/user-role.enum';
import { DoctorProfileService } from '../../core/services/doctor-profile.service';
import { SettingsService } from '../../core/services/settings.service';
import { StaffChatService } from '../../core/services/staff-chat.service';
import { NotificationService } from '../../core/services/notification.service';
import { ClinicStatusService } from '../../core/services/clinic-status.service';

@Component({
  selector: 'app-auth-layout',
  standalone: true,
  imports: [RouterOutlet, RouterLink, RouterLinkActive, CommonModule, TitleCasePipe],
  templateUrl: './authenticated-layout.component.html',
})
export class AuthenticatedLayout implements OnInit, OnDestroy {
  isMenuOpen = false;
  menuItems: MenuItem[] = [];
  expandedItems = new Set<string>();

  contextRoles: string[] = [];
  activeRole: string | null = '';
  clinicName = '';
  chatUnreadCount = 0;
  isClinicActive = false;
  isLinkedToClinic = false;

  private chatSubs = new Subscription();

  constructor(
    private router: Router,
    private permissionService: PermissionService,
    private doctorProfileService: DoctorProfileService,
    private menuService: MenuService,
    private settingsService: SettingsService,
    private staffChatService: StaffChatService,
    private notificationService: NotificationService,
    private clinicStatusService: ClinicStatusService,
  ) {}

  ngOnInit(): void {
    this.menuItems = this.menuService.getMenu();
    this.contextRoles = this.permissionService.getAvailableRoles();
    this.activeRole = this.permissionService.getActiveRole();
    this.loadClinicName();
    this.initChatNotifications();
    this.clinicStatusService.load();
    this.clinicStatusService.isClinicActive$.subscribe((active) => {
      this.isClinicActive = active;
    });
    this.clinicStatusService.hasClinic$.subscribe((has) => {
      this.isLinkedToClinic = has;
    });
  }

  ngOnDestroy(): void {
    this.chatSubs.unsubscribe();
  }

  private initChatNotifications(): void {
    this.staffChatService.connect();

    this.chatSubs.add(
      this.staffChatService.unreadCount$.subscribe((count) => {
        this.chatUnreadCount = count;
      }),
    );

    this.chatSubs.add(
      this.staffChatService.notification$.subscribe((msg) => {
        const isOnChatPage = this.router.url.includes('/staff-chat');
        if (!isOnChatPage) {
          const sender = msg.senderName ?? 'Team Chat';
          this.notificationService.info(`${msg.content?.substring(0, 60)}`, sender);
        }
      }),
    );

    // Reset unread count when the user navigates to the chat page
    this.chatSubs.add(
      this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe((e: any) => {
        if ((e as NavigationEnd).url?.includes('/staff-chat')) {
          this.staffChatService.setActiveConversation(null);
        }
      }),
    );
  }

  toggleExpand(label: string): void {
    if (this.expandedItems.has(label)) {
      this.expandedItems.delete(label);
    } else {
      this.expandedItems.add(label);
    }
  }

  isExpanded(label: string): boolean {
    return this.expandedItems.has(label);
  }

  toggleMenu() {
    this.isMenuOpen = !this.isMenuOpen;
  }

  switchRole(role: string): void {
    this.activeRole = role;
    this.expandedItems.delete('__role_switcher__');
    console.log('Switching to role:', role);
    this.doctorProfileService.setActiveProfileRole(role).subscribe();
    switch (role) {
      case 'DOCTOR': {
        const doctorRaw = localStorage.getItem('doctorLoad');
        const doctorData = doctorRaw ? JSON.parse(doctorRaw) : null;

        localStorage.setItem('userRole', 'DOCTOR');
        this.permissionService.setPermissions(UserRolePermissions[UserRole.DOCTOR]);

        this.router.navigate(['dashboard/doctor-dashboard'], {
          state: { doctorData },
        });
        break;
      }
      case 'MANAGER':
        this.router.navigate(['/dashboard/manager-dashboard']);
        localStorage.setItem('userRole', 'MANAGER');
        this.permissionService.setPermissions(UserRolePermissions[UserRole.MANAGER]);
        break;
      case 'RECEPTION':
        this.router.navigate(['/dashboard/reception-dashboard']);
        localStorage.setItem('userRole', 'RECEPTION');
        this.permissionService.setPermissions(UserRolePermissions[UserRole.RECEPTION]);
        break;
    }
  }

  logout() {
    this.staffChatService.disconnect();
    this.permissionService.clearPermissions();
    sessionStorage.clear();
    this.router.navigate(['/']);
  }

  navigateHome() {
    if (this.permissionService.has(Permissions.MANAGER)) {
      this.router.navigate(['/dashboard/manager-dashboard']);
    } else if (this.permissionService.has(Permissions.RECEPTION)) {
      this.router.navigate(['/dashboard/reception-dashboard']);
    } else if (this.permissionService.has(Permissions.DOCTOR)) {
      this.router.navigate(['/dashboard/doctor-dashboard']);
    }
  }

  private loadClinicName(): void {
    this.settingsService.getClinicProfile().subscribe({
      next: (clinicProfile) => {
        this.clinicName = clinicProfile.name?.trim() ?? '';
      },
      error: () => {
        this.clinicName = '';
      },
    });
  }
}
